import {z} from 'zod/v4';
import {
  CAMPAIGN_DESIGN_MD_RESERVED_FILE,
  CAMPAIGN_DESIGN_SYSTEM_LEGACY_PATH,
  CAMPAIGN_DESIGN_SYSTEM_SKILL_RESERVED_FILE,
  type CAMPAIGN_INSTRUCTIONS_ROOT_FOLDER,
} from './campaign-design-system-path';
import type {LIBRARY_INSTRUCTIONS_ROOT_FOLDER} from './constants';

/**
 * The product feature that owns a managed document, stamped when the service
 * projects it out of its reserved folder. Distinct from `origin`, which marks
 * a library-owned file with a different lock and copy.
 */
export const ManagedInstructionOwner = z.enum(['campaigns']);
export type ManagedInstructionOwner = z.infer<typeof ManagedInstructionOwner>;

// What the managed rule needs to know about the site. Passed in rather than
// looked up here so this module stays synchronous and free of I/O: the server
// resolves it from the site document, the Designer from `selectIsCampaignSite`.
export type ManagedInstructionSite = {isCampaignSite: boolean};

export type WebflowReservedRootFolder =
  | typeof CAMPAIGN_INSTRUCTIONS_ROOT_FOLDER
  | typeof LIBRARY_INSTRUCTIONS_ROOT_FOLDER;

// Stored at `${rootFolder}/${path}`, shown at `path`.
export type ManagedReservedFile = {
  managedBy: ManagedInstructionOwner;
  rootFolder: WebflowReservedRootFolder;
  path: string;
};

type ManagedInstructionCandidate = {
  path?: string;
  managedBy?: ManagedInstructionOwner;
};

/**
 * Answers which product feature, if any, owns an instruction.
 *
 * A managed document is readable in the Agent Instructions panel but rejected
 * by every write surface, because the feature that owns it has its own editor
 * and a freehand edit here would drift from it.
 *
 * Covers both kinds of managed document: one projected from a reserved root,
 * which carries `managedBy`, and one still at a legacy customer path. Every
 * read-only check, notice and mutation guard goes through `owner`, so
 * retiring the legacy paths only changes this class.
 */
export class ManagedInstructions {
  // Where to make the edit the panel won't accept, per owning feature.
  static readonly notices: Readonly<Record<ManagedInstructionOwner, string>> = {
    campaigns: 'Edit brand values in the Brand panel.',
  };

  // Documents a product feature owns in a reserved root.
  static readonly reservedFiles = [
    CAMPAIGN_DESIGN_SYSTEM_SKILL_RESERVED_FILE,
    CAMPAIGN_DESIGN_MD_RESERVED_FILE,
  ] as const satisfies readonly ManagedReservedFile[];

  // Documents the product writes on the customer's behalf at an ordinary
  // customer path. Listed by literal path: unlike a generated reference file,
  // nothing in the path shape marks a document as managed. Every entry belongs
  // to a campaign hub, so the site condition lives in `owner` rather than on
  // the entry. Delete once legacy hubs are backfilled to the reserved root.
  private static readonly legacyPaths = new Map<
    string,
    ManagedInstructionOwner
  >([[CAMPAIGN_DESIGN_SYSTEM_LEGACY_PATH, 'campaigns']]);

  // Skill folders a reserved root shows its files in, such as
  // `campaigns-design-system`.
  private static readonly reservedSkillNames = new Set(
    ManagedInstructions.reservedFiles.flatMap((file) =>
      file.path.split('/').slice(0, 1)
    )
  );

  static reservedFile(path: string): ManagedReservedFile | undefined {
    return ManagedInstructions.reservedFiles.find((file) => file.path === path);
  }

  static isInReservedSkill(path: string): boolean {
    return ManagedInstructions.reservedSkillNames.has(path.split('/')[0] ?? '');
  }

  static isLegacyPath(path: string): boolean {
    return ManagedInstructions.legacyPaths.has(path);
  }

  // Whether the path could be managed *for some site*, with no site lookup. A
  // caller that would otherwise load a site can skip the load when this is
  // false.
  static isCandidate(path: string): boolean {
    return (
      ManagedInstructions.isLegacyPath(path) ||
      ManagedInstructions.isInReservedSkill(path)
    );
  }

  static owner(
    instruction: ManagedInstructionCandidate | undefined,
    {isCampaignSite}: ManagedInstructionSite
  ): ManagedInstructionOwner | undefined {
    if (instruction?.managedBy) return instruction.managedBy;
    if (!isCampaignSite || instruction?.path === undefined) return undefined;
    return ManagedInstructions.legacyPaths.get(instruction.path);
  }

  // Undefined for a document that is not managed, and off a campaign hub for a
  // legacy path, so no one is pointed at a Brand panel their site does not
  // have.
  static notice(
    instruction: ManagedInstructionCandidate | undefined,
    site: ManagedInstructionSite
  ): string | undefined {
    const owner = ManagedInstructions.owner(instruction, site);
    return owner === undefined ? undefined : ManagedInstructions.notices[owner];
  }
}
